import {Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {PriceBasis} from '@dms/api/new-vehicle';
import i18n from '@dms/i18n';

import type {TestIdProps} from 'shared/web';

import {MoneyText, type MoneyTextProps} from '../MoneyText/MoneyText';

export type BasisPriceProps = {
  net: string;
  gross: string;
  basis: PriceBasis;
  currency: string;
  /** Rendered before the primary amount in the same text style. */
  prefix?: string;
  /** Replace zero amounts with the translated Included label. */
  showIncludedWhenZero?: boolean;
  size?: MoneyTextProps['size'];
  headingSize?: MoneyTextProps['headingSize'];
  alternative?: boolean;
  color?: MoneyTextProps['color'];
  align?: MoneyTextProps['align'];
  noWrap?: boolean;
} & TestIdProps;

const t = (key: string, defaultValue: string) => i18n.t(key, {defaultValue});

function PricePrefix(props: BasisPriceProps) {
  if (props.headingSize) {
    return (
      <Heading
        size={props.headingSize}
        color={props.color ?? undefined}
        align={props.align}
        isSingleLine={props.noWrap}
      >
        {props.prefix}
      </Heading>
    );
  }
  return (
    <Text
      size={props.size}
      alternative={props.alternative}
      color={props.color}
      align={props.align}
      noWrap={props.noWrap}
    >
      {props.prefix}
    </Text>
  );
}

export function BasisPrice(props: BasisPriceProps) {
  const formatCurrency = useFormatCurrency();
  // Money remains a decimal string in state; this conversion is display-only and is never used for arithmetic.
  const net = Number(props.net);
  const gross = Number(props.gross);
  const primary = props.basis === 'gross' ? props.gross : props.net;
  const secondary = props.basis === 'gross' ? net : gross;
  const suffix =
    props.basis === 'gross'
      ? t('entity.newVehicle.labels.amountNet', 'net')
      : t('entity.newVehicle.labels.amountGross', 'gross');

  if (props.showIncludedWhenZero && net === 0 && gross === 0) {
    return (
      <Text
        size={props.size}
        color="secondary"
        align={props.align}
        noWrap={props.noWrap}
        alternative={props.alternative}
        data-testid={props['data-testid']}
      >
        {t('entity.newVehicle.labels.included', 'Included')}
      </Text>
    );
  }

  return (
    <VStack
      spacing={0}
      align={props.align === 'right' ? 'flex-end' : 'flex-start'}
      data-testid={props['data-testid']}
    >
      <HStack spacing={1} align="baseline">
        <Show when={isNotNilOrEmpty(props.prefix) ? props.prefix : null}>
          {(prefix) => <PricePrefix {...props} prefix={prefix} />}
        </Show>
        <MoneyText
          amount={primary}
          currency={props.currency}
          size={props.size}
          headingSize={props.headingSize}
          alternative={props.alternative}
          color={props.color}
          align={props.align}
          noWrap={props.noWrap}
        />
      </HStack>
      <Show when={Number.isFinite(net) && Number.isFinite(gross)}>
        <Text size="xSmall" color="secondary" align={props.align} noWrap={props.noWrap}>
          {`${formatCurrency(secondary, props.currency, 2)} ${suffix}`}
        </Text>
      </Show>
    </VStack>
  );
}
